import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle, DialogTrigger 
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Activity, AlertTriangle, BarChart3, Bell, CheckCircle2, Clock,
  Database, Globe, Key, Lock, Mail, MessageSquare, RefreshCw,
  Server, Settings, Shield, TrendingUp, Users, Wifi, WifiOff,
  Zap, Ban, Eye, Trash2, Send, AlertCircle, XCircle, Loader2, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Chart colors
const CHART_COLORS = {
  primary: '#6366f1',
  secondary: '#8b5cf6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  muted: '#64748b'
};

const PIE_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#3b82f6'];

// Health status badge component
const HealthBadge = ({ status }) => {
  const config = {
    healthy: { color: 'bg-emerald-500', icon: CheckCircle2, label: 'Opérationnel' },
    degraded: { color: 'bg-amber-500', icon: AlertTriangle, label: 'Dégradé' },
    down: { color: 'bg-red-500', icon: XCircle, label: 'Hors ligne' }
  };
  
  const { color, icon: Icon, label } = config[status] || config.down;
  
  return (
    <div className="flex items-center gap-2">
      <div className={`w-2.5 h-2.5 rounded-full ${color} animate-pulse`} />
      <Icon className={`h-4 w-4 ${status === 'healthy' ? 'text-emerald-500' : status === 'degraded' ? 'text-amber-500' : 'text-red-500'}`} />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
};

// Stat card component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'indigo' }) => {
  const { isDarkMode } = useTheme();
  
  return (
    <Card className={`theme-transition card-hover ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)] glow-primary' : ''}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className={`text-sm font-medium ${isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}`}>{title}</p>
            <p className={`text-3xl font-bold mt-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{value}</p>
            {subtitle && (
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-[hsl(220,12%,45%)]' : 'text-slate-400'}`}>{subtitle}</p>
            )}
            {trend !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-xs ${trend >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                <TrendingUp className={`h-3 w-3 ${trend < 0 ? 'rotate-180' : ''}`} />
                <span>{Math.abs(trend)}% vs mois dernier</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-xl ${isDarkMode ? `bg-${color}-500/20` : `bg-${color}-100`}`}>
            <Icon className={`h-6 w-6 text-${color}-500`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SuperAdminDashboardV2 = () => {
  const { user, isSuperAdmin } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [config, setConfig] = useState(null);
  const [actions, setActions] = useState([]);
  const [ipRules, setIpRules] = useState([]);
  const [chartPeriod, setChartPeriod] = useState('24h');
  const [alerts, setAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  
  // Alert thresholds configuration
  const ALERT_THRESHOLDS = {
    responseTime: 200, // ms
    errorRate: 5, // %
    rateLimitedRate: 10, // %
    quotaUsage: 80, // %
    dbLatency: 100, // ms
  };
  
  // Broadcast dialog
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastTitle, setBroadcastTitle] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastTarget, setBroadcastTarget] = useState('all');
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  
  // IP Rule dialog
  const [ipRuleOpen, setIpRuleOpen] = useState(false);
  const [newIpPattern, setNewIpPattern] = useState('');
  const [newIpAction, setNewIpAction] = useState('deny');
  
  // Calculate alerts based on metrics
  const calculateAlerts = useCallback((metricsData, healthData, statsData) => {
    const newAlerts = [];
    
    // Response time alert
    if (metricsData?.avg_response_time_ms > ALERT_THRESHOLDS.responseTime) {
      newAlerts.push({
        id: 'response_time',
        type: 'warning',
        title: 'Temps de réponse élevé',
        message: `Le temps de réponse moyen est de ${metricsData.avg_response_time_ms.toFixed(0)}ms (seuil: ${ALERT_THRESHOLDS.responseTime}ms)`,
        value: metricsData.avg_response_time_ms,
        threshold: ALERT_THRESHOLDS.responseTime,
        icon: Clock
      });
    }
    
    // Error rate alert
    if (metricsData?.total_requests > 0) {
      const errorRate = (metricsData.failed_requests / metricsData.total_requests) * 100;
      if (errorRate > ALERT_THRESHOLDS.errorRate) {
        newAlerts.push({
          id: 'error_rate',
          type: 'danger',
          title: 'Taux d\'erreur élevé',
          message: `${errorRate.toFixed(1)}% des requêtes échouent (seuil: ${ALERT_THRESHOLDS.errorRate}%)`,
          value: errorRate,
          threshold: ALERT_THRESHOLDS.errorRate,
          icon: AlertTriangle
        });
      }
    }
    
    // Rate limited requests alert
    if (metricsData?.total_requests > 0) {
      const rateLimitedRate = (metricsData.rate_limited_requests / metricsData.total_requests) * 100;
      if (rateLimitedRate > ALERT_THRESHOLDS.rateLimitedRate) {
        newAlerts.push({
          id: 'rate_limited',
          type: 'warning',
          title: 'Nombreuses requêtes limitées',
          message: `${rateLimitedRate.toFixed(1)}% des requêtes sont rate-limitées (seuil: ${ALERT_THRESHOLDS.rateLimitedRate}%)`,
          value: rateLimitedRate,
          threshold: ALERT_THRESHOLDS.rateLimitedRate,
          icon: Ban
        });
      }
    }
    
    // Database latency alert
    if (healthData?.database_latency_ms > ALERT_THRESHOLDS.dbLatency) {
      newAlerts.push({
        id: 'db_latency',
        type: 'warning',
        title: 'Latence base de données',
        message: `La latence DB est de ${healthData.database_latency_ms.toFixed(0)}ms (seuil: ${ALERT_THRESHOLDS.dbLatency}ms)`,
        value: healthData.database_latency_ms,
        threshold: ALERT_THRESHOLDS.dbLatency,
        icon: Database
      });
    }
    
    // System health alerts
    if (healthData?.database === 'down') {
      newAlerts.push({
        id: 'db_down',
        type: 'critical',
        title: 'Base de données hors ligne',
        message: 'La connexion à la base de données est perdue',
        icon: Database
      });
    }
    
    if (healthData?.email_service === 'degraded') {
      newAlerts.push({
        id: 'email_degraded',
        type: 'info',
        title: 'Service email dégradé',
        message: 'Le service email Resend nécessite une vérification de domaine',
        icon: Mail
      });
    }
    
    // Delivery rate alert
    if (statsData?.delivery_rate < 90 && statsData?.total_messages_sent > 100) {
      newAlerts.push({
        id: 'delivery_rate',
        type: 'warning',
        title: 'Taux de livraison bas',
        message: `Seulement ${statsData.delivery_rate.toFixed(1)}% des messages sont livrés`,
        value: statsData.delivery_rate,
        threshold: 90,
        icon: MessageSquare
      });
    }
    
    return newAlerts;
  }, []);
  
  // Dismiss alert
  const dismissAlert = (alertId) => {
    setDismissedAlerts(prev => new Set([...prev, alertId]));
  };
  
  // Filtered alerts (excluding dismissed ones)
  const activeAlerts = useMemo(() => {
    return alerts.filter(alert => !dismissedAlerts.has(alert.id));
  }, [alerts, dismissedAlerts]);
  
  // Generate mock chart data based on real metrics
  const apiRequestsData = useMemo(() => {
    const hours = chartPeriod === '24h' ? 24 : chartPeriod === '7d' ? 7 : 30;
    const isHourly = chartPeriod === '24h';
    const baseRequests = metrics?.total_requests || 1000;
    const avgPerPeriod = baseRequests / hours;
    
    return Array.from({ length: hours }, (_, i) => {
      const variance = 0.3 + Math.random() * 0.7;
      const requests = Math.round(avgPerPeriod * variance);
      const successRate = 0.92 + Math.random() * 0.07;
      
      return {
        time: isHourly ? `${String(i).padStart(2, '0')}h` : `J-${hours - i}`,
        requests,
        successful: Math.round(requests * successRate),
        failed: Math.round(requests * (1 - successRate)),
      };
    });
  }, [metrics, chartPeriod]);
  
  const messagesData = useMemo(() => {
    const days = 7;
    const baseMessages = stats?.messages_this_month || 5000;
    const avgPerDay = baseMessages / 30;
    
    return Array.from({ length: days }, (_, i) => {
      const variance = 0.5 + Math.random() * 1;
      const sent = Math.round(avgPerDay * variance);
      const delivered = Math.round(sent * (0.85 + Math.random() * 0.1));
      const read = Math.round(delivered * (0.4 + Math.random() * 0.3));
      
      const date = new Date();
      date.setDate(date.getDate() - (days - 1 - i));
      
      return {
        day: date.toLocaleDateString('fr-FR', { weekday: 'short' }),
        sent,
        delivered,
        read,
      };
    });
  }, [stats]);
  
  const responseTimeData = useMemo(() => {
    const avgTime = metrics?.avg_response_time_ms || 50;
    
    return [
      { range: '0-20ms', count: Math.round(Math.random() * 200 + 100), fill: CHART_COLORS.success },
      { range: '20-50ms', count: Math.round(Math.random() * 300 + 200), fill: CHART_COLORS.primary },
      { range: '50-100ms', count: Math.round(Math.random() * 150 + 50), fill: CHART_COLORS.info },
      { range: '100-200ms', count: Math.round(Math.random() * 80 + 20), fill: CHART_COLORS.warning },
      { range: '200ms+', count: Math.round(Math.random() * 30 + 5), fill: CHART_COLORS.danger },
    ];
  }, [metrics]);
  
  const errorBreakdownData = useMemo(() => {
    const errors = metrics?.error_breakdown || {};
    const errorData = [
      { name: '400 Bad Request', value: errors[400] || Math.round(Math.random() * 50) },
      { name: '401 Unauthorized', value: errors[401] || Math.round(Math.random() * 30) },
      { name: '403 Forbidden', value: errors[403] || Math.round(Math.random() * 20) },
      { name: '404 Not Found', value: errors[404] || Math.round(Math.random() * 40) },
      { name: '500 Server Error', value: errors[500] || Math.round(Math.random() * 15) },
    ];
    
    return errorData.filter(e => e.value > 0);
  }, [metrics]);
  
  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label, isDark }) => {
    if (!active || !payload?.length) return null;
    
    return (
      <div className={`px-3 py-2 rounded-lg shadow-lg border ${
        isDark 
          ? 'bg-[hsl(220,17%,18%)] border-[hsl(220,14%,25%)] text-white' 
          : 'bg-white border-slate-200 text-slate-900'
      }`}>
        <p className="font-medium text-sm mb-1">{label}</p>
        {payload.map((entry, idx) => (
          <p key={idx} className="text-xs" style={{ color: entry.color }}>
            {entry.name}: {entry.value?.toLocaleString()}
          </p>
        ))}
      </div>
    );
  };
  const [newIpReason, setNewIpReason] = useState('');
  
  const token = localStorage.getItem('token');

  // Fetch all data
  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [statsRes, healthRes, metricsRes, configRes, actionsRes, ipRulesRes] = await Promise.allSettled([
        axios.get(`${API_URL}/v2/supadmin/stats`, { headers }),
        axios.get(`${API_URL}/v2/supadmin/health`, { headers }),
        axios.get(`${API_URL}/v2/gateway/metrics?period=day`, { headers }),
        axios.get(`${API_URL}/v2/supadmin/config`, { headers }),
        axios.get(`${API_URL}/v2/supadmin/actions?limit=10`, { headers }),
        axios.get(`${API_URL}/v2/gateway/ip-rules`, { headers })
      ]);
      
      const newStats = statsRes.status === 'fulfilled' ? statsRes.value.data : null;
      const newHealth = healthRes.status === 'fulfilled' ? healthRes.value.data : null;
      const newMetrics = metricsRes.status === 'fulfilled' ? metricsRes.value.data : null;
      
      if (newStats) setStats(newStats);
      if (newHealth) setHealth(newHealth);
      if (newMetrics) setMetrics(newMetrics);
      if (configRes.status === 'fulfilled') setConfig(configRes.value.data);
      if (actionsRes.status === 'fulfilled') setActions(actionsRes.value.data.actions || []);
      if (ipRulesRes.status === 'fulfilled') setIpRules(ipRulesRes.value.data || []);
      
      // Calculate alerts based on new data
      const newAlerts = calculateAlerts(newMetrics, newHealth, newStats);
      setAlerts(newAlerts);
      
      // Show toast for critical alerts
      newAlerts.filter(a => a.type === 'critical' || a.type === 'danger').forEach(alert => {
        if (!dismissedAlerts.has(alert.id)) {
          toast.error(alert.title, { description: alert.message });
        }
      });
      
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, calculateAlerts, dismissedAlerts]);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => fetchData(), 30000);
    return () => clearInterval(interval);
  }, [isSuperAdmin, navigate, fetchData]);

  // Toggle maintenance mode
  const toggleMaintenance = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const endpoint = config?.maintenance_mode ? 'disable' : 'enable';
      await axios.post(`${API_URL}/v2/supadmin/maintenance/${endpoint}`, {}, { headers });
      toast.success(`Mode maintenance ${endpoint === 'enable' ? 'activé' : 'désactivé'}`);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors du changement de mode');
    }
  };

  // Send broadcast
  const sendBroadcast = async () => {
    if (!broadcastTitle || !broadcastMessage) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    const headers = { Authorization: `Bearer ${token}` };
    setSendingBroadcast(true);
    try {
      const res = await axios.post(`${API_URL}/v2/supadmin/broadcast`, {
        title: broadcastTitle,
        message: broadcastMessage,
        target: broadcastTarget
      }, { headers });
      
      toast.success(res.data.message);
      setBroadcastOpen(false);
      setBroadcastTitle('');
      setBroadcastMessage('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingBroadcast(false);
    }
  };

  // Create IP rule
  const createIpRule = async () => {
    if (!newIpPattern) {
      toast.error('Veuillez entrer une adresse IP');
      return;
    }
    
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.post(`${API_URL}/v2/gateway/ip-rules`, {
        ip_pattern: newIpPattern,
        action: newIpAction,
        reason: newIpReason || undefined
      }, { headers });
      
      toast.success('Règle IP créée');
      setIpRuleOpen(false);
      setNewIpPattern('');
      setNewIpReason('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  // Delete IP rule
  const deleteIpRule = async (ruleId) => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.delete(`${API_URL}/v2/gateway/ip-rules/${ruleId}`, { headers });
      toast.success('Règle supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Format uptime
  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}j ${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 theme-fade-in" data-testid="supadmin-dashboard">
        {/* Alerts Banner */}
        {activeAlerts.length > 0 && (
          <div className="space-y-3" data-testid="alerts-container">
            {activeAlerts.map((alert) => {
              const AlertIcon = alert.icon || AlertTriangle;
              const bgColors = {
                critical: isDarkMode ? 'bg-red-950/40 border-red-900/50' : 'bg-red-50 border-red-200',
                danger: isDarkMode ? 'bg-red-950/30 border-red-900/40' : 'bg-red-50 border-red-200',
                warning: isDarkMode ? 'bg-amber-950/30 border-amber-900/40' : 'bg-amber-50 border-amber-200',
                info: isDarkMode ? 'bg-blue-950/30 border-blue-900/40' : 'bg-blue-50 border-blue-200'
              };
              const textColors = {
                critical: 'text-red-500',
                danger: 'text-red-400',
                warning: 'text-amber-400',
                info: 'text-blue-400'
              };
              
              return (
                <div
                  key={alert.id}
                  className={`flex items-start gap-4 p-4 rounded-xl border ${bgColors[alert.type]} animate-in slide-in-from-top-2`}
                >
                  <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-[hsl(220,13%,16%)]' : 'bg-white/50'}`}>
                    <AlertIcon className={`h-5 w-5 ${textColors[alert.type]}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className={`font-semibold ${isDarkMode ? 'text-[hsl(220,13%,82%)]' : 'text-slate-900'}`}>
                        {alert.title}
                      </h4>
                      {alert.type === 'critical' && (
                        <Badge variant="destructive" className="text-xs animate-pulse">CRITIQUE</Badge>
                      )}
                    </div>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-[hsl(220,12%,60%)]' : 'text-slate-600'}`}>
                      {alert.message}
                    </p>
                    {alert.value !== undefined && (
                      <div className="mt-2">
                        <Progress 
                          value={Math.min((alert.value / (alert.threshold * 2)) * 100, 100)} 
                          className={`h-1.5 ${isDarkMode ? 'bg-[hsl(220,13%,20%)]' : ''}`}
                        />
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => dismissAlert(alert.id)}
                    className={`shrink-0 ${isDarkMode ? 'hover:bg-[hsl(220,13%,20%)]' : ''}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-[hsl(220,13%,85%)]' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
              Administration Avancée
            </h1>
            <p className={`mt-1 ${isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}`}>
              Surveillance et gestion de la plateforme B2RCS
            </p>
          </div>
          <div className="flex items-center gap-3">
            {activeAlerts.length > 0 && (
              <Badge variant="outline" className={`${isDarkMode ? 'border-amber-500/50 text-amber-400' : 'border-amber-500 text-amber-600'}`}>
                <AlertCircle className="h-3 w-3 mr-1" />
                {activeAlerts.length} alerte{activeAlerts.length > 1 ? 's' : ''}
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className={isDarkMode ? 'border-[hsl(220,13%,22%)] hover:bg-[hsl(220,13%,18%)]' : ''}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            
            <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Bell className="h-4 w-4 mr-2" />
                  Broadcast
                </Button>
              </DialogTrigger>
              <DialogContent className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,13%,20%)]' : ''}>
                <DialogHeader>
                  <DialogTitle className={isDarkMode ? 'text-[hsl(220,13%,85%)]' : ''}>Envoyer un message broadcast</DialogTitle>
                  <DialogDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : ''}>
                    Ce message sera envoyé à tous les utilisateurs sélectionnés.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className={isDarkMode ? 'text-[hsl(220,13%,70%)]' : ''}>Titre</Label>
                    <Input
                      value={broadcastTitle}
                      onChange={(e) => setBroadcastTitle(e.target.value)}
                      placeholder="Titre du message"
                      className={isDarkMode ? 'bg-[hsl(220,13%,16%)] border-[hsl(220,13%,22%)] text-[hsl(220,13%,80%)]' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={isDarkMode ? 'text-[hsl(210,20%,90%)]' : ''}>Message</Label>
                    <Textarea
                      value={broadcastMessage}
                      onChange={(e) => setBroadcastMessage(e.target.value)}
                      placeholder="Contenu du message..."
                      rows={4}
                      className={isDarkMode ? 'bg-[hsl(220,14%,20%)] border-[hsl(220,14%,25%)] text-white' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className={isDarkMode ? 'text-[hsl(210,20%,90%)]' : ''}>Destinataires</Label>
                    <div className="flex gap-2">
                      {['all', 'admins'].map((target) => (
                        <Button
                          key={target}
                          type="button"
                          variant={broadcastTarget === target ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setBroadcastTarget(target)}
                          className={broadcastTarget !== target && isDarkMode ? 'border-[hsl(220,14%,25%)]' : ''}
                        >
                          {target === 'all' ? 'Tous' : 'Admins uniquement'}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setBroadcastOpen(false)}>Annuler</Button>
                  <Button onClick={sendBroadcast} disabled={sendingBroadcast}>
                    {sendingBroadcast ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Envoyer
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* System Health Overview */}
        <Card className={`theme-transition ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                  <Activity className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <CardTitle className={isDarkMode ? 'text-white' : ''}>Santé du Système</CardTitle>
                  <CardDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : ''}>
                    État en temps réel des services
                  </CardDescription>
                </div>
              </div>
              <HealthBadge status={health?.overall || 'down'} />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {/* Database */}
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Database className={`h-4 w-4 ${health?.database === 'healthy' ? 'text-emerald-500' : 'text-red-500'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : ''}`}>Base de données</span>
                </div>
                <HealthBadge status={health?.database || 'down'} />
                <p className={`text-xs mt-2 ${isDarkMode ? 'text-[hsl(220,12%,45%)]' : 'text-slate-400'}`}>
                  Latence: {health?.database_latency_ms?.toFixed(1)}ms
                </p>
              </div>
              
              {/* RCS Adapter */}
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className={`h-4 w-4 ${health?.rcs_adapter === 'healthy' ? 'text-emerald-500' : 'text-amber-500'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : ''}`}>RCS Adapter</span>
                </div>
                <HealthBadge status={health?.rcs_adapter || 'down'} />
                <span className={`text-xs mt-2 block ${isDarkMode ? 'text-[hsl(220,12%,45%)]' : 'text-slate-400'}`}>
                  Mode: <Badge variant="outline" className="ml-1 text-xs">{health?.rcs_adapter_mode || 'N/A'}</Badge>
                </span>
              </div>
              
              {/* Email Service */}
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Mail className={`h-4 w-4 ${health?.email_service === 'healthy' ? 'text-emerald-500' : 'text-amber-500'}`} />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : ''}`}>Email (Resend)</span>
                </div>
                <HealthBadge status={health?.email_service || 'down'} />
              </div>
              
              {/* Uptime */}
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-4 w-4 text-indigo-500" />
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : ''}`}>Uptime</span>
                </div>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  {formatUptime(health?.uptime_seconds || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Clients Actifs"
            value={stats?.active_clients || 0}
            subtitle={`${stats?.total_clients || 0} total`}
            icon={Users}
            color="indigo"
          />
          <StatCard
            title="Messages Aujourd'hui"
            value={stats?.messages_today?.toLocaleString() || 0}
            subtitle={`${stats?.messages_this_month?.toLocaleString() || 0} ce mois`}
            icon={MessageSquare}
            color="emerald"
          />
          <StatCard
            title="Taux de Livraison"
            value={`${stats?.delivery_rate?.toFixed(1) || 0}%`}
            subtitle={`Lecture: ${stats?.read_rate?.toFixed(1) || 0}%`}
            icon={Zap}
            color="amber"
          />
          <StatCard
            title="Campagnes Actives"
            value={stats?.active_campaigns || 0}
            subtitle={`${stats?.total_campaigns || 0} total`}
            icon={BarChart3}
            color="purple"
          />
        </div>

        {/* Tabs Section */}
        <Tabs defaultValue="api" className="w-full">
          <TabsList className={`grid w-full grid-cols-4 ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : ''}`}>
            <TabsTrigger value="api" className={isDarkMode ? 'data-[state=active]:bg-[hsl(220,14%,14%)]' : ''}>
              <Globe className="h-4 w-4 mr-2" />
              API Metrics
            </TabsTrigger>
            <TabsTrigger value="security" className={isDarkMode ? 'data-[state=active]:bg-[hsl(220,14%,14%)]' : ''}>
              <Shield className="h-4 w-4 mr-2" />
              Sécurité
            </TabsTrigger>
            <TabsTrigger value="actions" className={isDarkMode ? 'data-[state=active]:bg-[hsl(220,14%,14%)]' : ''}>
              <Eye className="h-4 w-4 mr-2" />
              Actions
            </TabsTrigger>
            <TabsTrigger value="config" className={isDarkMode ? 'data-[state=active]:bg-[hsl(220,14%,14%)]' : ''}>
              <Settings className="h-4 w-4 mr-2" />
              Configuration
            </TabsTrigger>
          </TabsList>

          {/* API Metrics Tab */}
          <TabsContent value="api" className="mt-6 space-y-6">
            {/* Period Selector */}
            <div className="flex justify-end">
              <div className="flex gap-1 p-1 rounded-lg bg-slate-100 dark:bg-[hsl(220,13%,17%)]">
                {['24h', '7d', '30d'].map((period) => (
                  <Button
                    key={period}
                    variant={chartPeriod === period ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setChartPeriod(period)}
                    className={chartPeriod !== period ? 'hover:bg-slate-200 dark:hover:bg-[hsl(220,14%,22%)]' : ''}
                  >
                    {period}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* API Requests Chart */}
            <Card className={`theme-transition ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>Requêtes API</CardTitle>
                    <CardDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : ''}>
                      Volume des requêtes sur {chartPeriod === '24h' ? 'les dernières 24 heures' : chartPeriod === '7d' ? 'les 7 derniers jours' : 'les 30 derniers jours'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-indigo-500" />
                      <span className={isDarkMode ? 'text-[hsl(215,16%,70%)]' : 'text-slate-600'}>Total</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                      <span className={isDarkMode ? 'text-[hsl(215,16%,70%)]' : 'text-slate-600'}>Succès</span>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={apiRequestsData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorSuccess" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={CHART_COLORS.success} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={CHART_COLORS.success} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'hsl(220,14%,22%)' : '#e2e8f0'} />
                      <XAxis 
                        dataKey="time" 
                        stroke={isDarkMode ? 'hsl(215,16%,50%)' : '#94a3b8'}
                        tick={{ fill: isDarkMode ? 'hsl(215,16%,60%)' : '#64748b', fontSize: 12 }}
                      />
                      <YAxis 
                        stroke={isDarkMode ? 'hsl(215,16%,50%)' : '#94a3b8'}
                        tick={{ fill: isDarkMode ? 'hsl(215,16%,60%)' : '#64748b', fontSize: 12 }}
                      />
                      <Tooltip content={<CustomTooltip isDark={isDarkMode} />} />
                      <Area 
                        type="monotone" 
                        dataKey="requests" 
                        name="Requêtes"
                        stroke={CHART_COLORS.primary} 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#colorRequests)" 
                      />
                      <Area 
                        type="monotone" 
                        dataKey="successful" 
                        name="Succès"
                        stroke={CHART_COLORS.success} 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#colorSuccess)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Messages Evolution Chart */}
              <Card className={`theme-transition ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
                <CardHeader>
                  <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>Messages RCS (7 jours)</CardTitle>
                  <CardDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : ''}>
                    Envoyés, livrés et lus
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={messagesData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'hsl(220,14%,22%)' : '#e2e8f0'} />
                        <XAxis 
                          dataKey="day" 
                          stroke={isDarkMode ? 'hsl(215,16%,50%)' : '#94a3b8'}
                          tick={{ fill: isDarkMode ? 'hsl(215,16%,60%)' : '#64748b', fontSize: 12 }}
                        />
                        <YAxis 
                          stroke={isDarkMode ? 'hsl(215,16%,50%)' : '#94a3b8'}
                          tick={{ fill: isDarkMode ? 'hsl(215,16%,60%)' : '#64748b', fontSize: 12 }}
                        />
                        <Tooltip content={<CustomTooltip isDark={isDarkMode} />} />
                        <Legend 
                          wrapperStyle={{ 
                            paddingTop: '10px',
                            color: isDarkMode ? 'hsl(215,16%,70%)' : '#64748b'
                          }}
                        />
                        <Bar dataKey="sent" name="Envoyés" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="delivered" name="Livrés" fill={CHART_COLORS.success} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="read" name="Lus" fill={CHART_COLORS.info} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Response Time Distribution */}
              <Card className={`theme-transition ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
                <CardHeader>
                  <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>Distribution Temps de Réponse</CardTitle>
                  <CardDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : ''}>
                    Répartition des latences API
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={responseTimeData} layout="vertical" margin={{ top: 10, right: 30, left: 50, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? 'hsl(220,14%,22%)' : '#e2e8f0'} />
                        <XAxis 
                          type="number"
                          stroke={isDarkMode ? 'hsl(215,16%,50%)' : '#94a3b8'}
                          tick={{ fill: isDarkMode ? 'hsl(215,16%,60%)' : '#64748b', fontSize: 12 }}
                        />
                        <YAxis 
                          type="category"
                          dataKey="range"
                          stroke={isDarkMode ? 'hsl(215,16%,50%)' : '#94a3b8'}
                          tick={{ fill: isDarkMode ? 'hsl(215,16%,60%)' : '#64748b', fontSize: 11 }}
                          width={60}
                        />
                        <Tooltip content={<CustomTooltip isDark={isDarkMode} />} />
                        <Bar 
                          dataKey="count" 
                          name="Requêtes"
                          radius={[0, 4, 4, 0]}
                        >
                          {responseTimeData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Error Breakdown Pie Chart */}
              <Card className={`theme-transition ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
                <CardHeader>
                  <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>Répartition des Erreurs</CardTitle>
                  <CardDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : ''}>
                    Par code de statut HTTP
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-center">
                    {errorBreakdownData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={errorBreakdownData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {errorBreakdownData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip isDark={isDarkMode} />} />
                          <Legend 
                            layout="vertical" 
                            align="right" 
                            verticalAlign="middle"
                            wrapperStyle={{ 
                              paddingLeft: '20px',
                              fontSize: '12px',
                              color: isDarkMode ? 'hsl(215,16%,70%)' : '#64748b'
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className={`w-full text-center ${isDarkMode ? 'text-[hsl(220,12%,45%)]' : 'text-slate-400'}`}>
                        <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-500 opacity-50" />
                        <p>Aucune erreur détectée</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {/* Quick Stats Card */}
              <Card className={`theme-transition ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
                <CardHeader>
                  <CardTitle className={`text-lg ${isDarkMode ? 'text-white' : ''}`}>Résumé Rapide</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : 'bg-slate-50'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}`}>Total Requêtes</p>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : ''}`}>
                        {metrics?.total_requests?.toLocaleString() || 0}
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : 'bg-slate-50'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}`}>Taux Succès</p>
                      <p className="text-2xl font-bold text-emerald-500">
                        {metrics?.total_requests > 0 
                          ? ((metrics.successful_requests / metrics.total_requests) * 100).toFixed(1) 
                          : 100}%
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : 'bg-slate-50'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}`}>Temps Moyen</p>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : ''}`}>
                        {metrics?.avg_response_time_ms?.toFixed(0) || 0}<span className="text-sm ml-1">ms</span>
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : 'bg-slate-50'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}`}>Rate Limited</p>
                      <p className="text-2xl font-bold text-amber-500">
                        {metrics?.rate_limited_requests?.toLocaleString() || 0}
                      </p>
                    </div>
                  </div>
                  
                  {/* Top Endpoints */}
                  <div>
                    <p className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-white' : ''}`}>Top Endpoints</p>
                    <div className="space-y-2">
                      {(metrics?.top_endpoints || []).slice(0, 4).map((endpoint, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <code className={`text-xs px-2 py-1 rounded truncate max-w-[200px] ${isDarkMode ? 'bg-[hsl(220,14%,20%)] text-indigo-400' : 'bg-slate-100 text-slate-700'}`}>
                            {endpoint.endpoint}
                          </code>
                          <Badge variant="secondary">{endpoint.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="mt-6">
            <Card className={`theme-transition ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className={isDarkMode ? 'text-white' : ''}>Règles IP</CardTitle>
                    <CardDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : ''}>
                      Gérer les accès par adresse IP
                    </CardDescription>
                  </div>
                  <Dialog open={ipRuleOpen} onOpenChange={setIpRuleOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Ban className="h-4 w-4 mr-2" />
                        Nouvelle règle
                      </Button>
                    </DialogTrigger>
                    <DialogContent className={isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}>
                      <DialogHeader>
                        <DialogTitle className={isDarkMode ? 'text-white' : ''}>Créer une règle IP</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label className={isDarkMode ? 'text-[hsl(210,20%,90%)]' : ''}>Adresse IP / Pattern</Label>
                          <Input
                            value={newIpPattern}
                            onChange={(e) => setNewIpPattern(e.target.value)}
                            placeholder="192.168.1.1 ou 192.168.1.0/24"
                            className={isDarkMode ? 'bg-[hsl(220,14%,20%)] border-[hsl(220,14%,25%)] text-white' : ''}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className={isDarkMode ? 'text-[hsl(210,20%,90%)]' : ''}>Action</Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant={newIpAction === 'deny' ? 'destructive' : 'outline'}
                              size="sm"
                              onClick={() => setNewIpAction('deny')}
                            >
                              <Ban className="h-4 w-4 mr-1" /> Bloquer
                            </Button>
                            <Button
                              type="button"
                              variant={newIpAction === 'allow' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setNewIpAction('allow')}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" /> Autoriser
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className={isDarkMode ? 'text-[hsl(210,20%,90%)]' : ''}>Raison (optionnel)</Label>
                          <Input
                            value={newIpReason}
                            onChange={(e) => setNewIpReason(e.target.value)}
                            placeholder="Tentatives de brute force..."
                            className={isDarkMode ? 'bg-[hsl(220,14%,20%)] border-[hsl(220,14%,25%)] text-white' : ''}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setIpRuleOpen(false)}>Annuler</Button>
                        <Button onClick={createIpRule}>Créer</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {ipRules.map((rule) => (
                    <div
                      key={rule.id}
                      className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : 'bg-slate-50'}`}
                    >
                      <div className="flex items-center gap-4">
                        {rule.action === 'deny' ? (
                          <div className="p-2 rounded-lg bg-red-100 dark:bg-red-950/50">
                            <Ban className="h-4 w-4 text-red-500" />
                          </div>
                        ) : (
                          <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-950/50">
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          </div>
                        )}
                        <div>
                          <code className={`font-mono ${isDarkMode ? 'text-white' : ''}`}>{rule.ip_pattern}</code>
                          {rule.reason && (
                            <p className={`text-xs mt-1 ${isDarkMode ? 'text-[hsl(220,12%,45%)]' : 'text-slate-400'}`}>
                              {rule.reason}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={rule.action === 'deny' ? 'destructive' : 'default'}>
                          {rule.action === 'deny' ? 'Bloqué' : 'Autorisé'}
                        </Badge>
                        <span className={`text-xs ${isDarkMode ? 'text-[hsl(220,12%,45%)]' : 'text-slate-400'}`}>
                          {rule.hit_count} hits
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteIpRule(rule.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {ipRules.length === 0 && (
                    <div className={`text-center py-8 ${isDarkMode ? 'text-[hsl(220,12%,45%)]' : 'text-slate-400'}`}>
                      <Shield className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Aucune règle IP configurée</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Actions Log Tab */}
          <TabsContent value="actions" className="mt-6">
            <Card className={`theme-transition ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
              <CardHeader>
                <CardTitle className={isDarkMode ? 'text-white' : ''}>Journal des Actions Admin</CardTitle>
                <CardDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : ''}>
                  Dernières actions administratives
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {actions.map((action, idx) => (
                    <div
                      key={action.id || idx}
                      className={`flex items-start gap-4 p-4 rounded-lg ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : 'bg-slate-50'}`}
                    >
                      <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'}`}>
                        <Activity className="h-4 w-4 text-indigo-500" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>{action.admin_email}</span>
                          <Badge variant="outline" className="text-xs">{action.action_type}</Badge>
                        </div>
                        <p className={`text-sm mt-1 ${isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}`}>
                          {action.description}
                        </p>
                        <p className={`text-xs mt-2 ${isDarkMode ? 'text-[hsl(215,16%,45%)]' : 'text-slate-400'}`}>
                          {new Date(action.created_at).toLocaleString('fr-FR')}
                        </p>
                      </div>
                    </div>
                  ))}
                  {actions.length === 0 && (
                    <div className={`text-center py-8 ${isDarkMode ? 'text-[hsl(220,12%,45%)]' : 'text-slate-400'}`}>
                      <Eye className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Aucune action récente</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Config Tab */}
          <TabsContent value="config" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className={`theme-transition ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
                <CardHeader>
                  <CardTitle className={isDarkMode ? 'text-white' : ''}>Mode Maintenance</CardTitle>
                  <CardDescription className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : ''}>
                    Bloquer temporairement l'accès à la plateforme
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-4 rounded-lg border dark:border-[hsl(220,14%,22%)]">
                    <div className="flex items-center gap-3">
                      {config?.maintenance_mode ? (
                        <WifiOff className="h-5 w-5 text-amber-500" />
                      ) : (
                        <Wifi className="h-5 w-5 text-emerald-500" />
                      )}
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>
                          {config?.maintenance_mode ? 'Maintenance active' : 'Plateforme en ligne'}
                        </p>
                        <p className={`text-sm ${isDarkMode ? 'text-[hsl(220,12%,45%)]' : 'text-slate-400'}`}>
                          {config?.maintenance_message || 'Aucun message configuré'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={config?.maintenance_mode || false}
                      onCheckedChange={toggleMaintenance}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className={`theme-transition ${isDarkMode ? 'bg-[hsl(220,14%,14%)] border-[hsl(220,14%,22%)]' : ''}`}>
                <CardHeader>
                  <CardTitle className={isDarkMode ? 'text-white' : ''}>Configuration RCS</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : 'bg-slate-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}>Mode RCS</span>
                      <Badge variant={config?.rcs_mode === 'live' ? 'default' : 'secondary'}>
                        {config?.rcs_mode || 'mock'}
                      </Badge>
                    </div>
                  </div>
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : 'bg-slate-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}>Quota par défaut</span>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>
                        {config?.default_monthly_quota?.toLocaleString() || 10000} msg/mois
                      </span>
                    </div>
                  </div>
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[hsl(220,13%,17%)]' : 'bg-slate-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className={isDarkMode ? 'text-[hsl(220,12%,55%)]' : 'text-slate-500'}>Rate limit</span>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : ''}`}>
                        {config?.default_rate_limit || 100} msg/min
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default SuperAdminDashboardV2;
